import {
    SelectionMode,
    SingleSubselect,
    SingleSubselectListener,
    TreeSelectDelegate,
    TreeSelection,
} from "./tree-select";
import { Branch } from "./branch";

/**
 * Responsibilities include
 * - keeping track of the current selected value
 * - loading sublevels of the tree when values with children are selected.
 * - ensuring that no more than one value is selected across all levels of the tree at a given time
 * - ensuring that only the current branch of nodes are visible at a given time
 */

export class SingleSelectionMode implements SelectionMode<Branch>, SingleSubselectListener {

    constructor(
        public readonly element: HTMLElement,
        private delegate: TreeSelectDelegate<SingleSubselect>,
        public readonly root: SingleSubselect,
        private subselects: SingleSubselect[] = [root]
    ) { }

    connect(): void {
        // Connect all subselects in the current branch (including root)
        this.subselects.forEach(subselect => subselect.connect(this));
    }

    disconnect(): void {
        // Disconnect all node selects in the current branch (including root)
        // Otherwise we might introduce duplicate event listeners when reconnecting
        this.subselects.forEach(subselect => subselect.disconnect());
    }

    get value(): Branch {
        return Branch.fromValues(this.subselects.map(subselect => subselect.value));
    }

    set value(value: Branch | null) {
        
        // If null or empty value is passed, clear reset to root node
        if (!value || value.empty) {
            this.root.value = null;
            this.clearDescendants(0);
            return;
        }

        // If the value did not change, avoid unnecessary requests and DOM changes
        if (value.equals(this.value)) {
            return;
        }

        // Update the branch to match the new value. This may involve loading new subselects if the new value has a different structure from the previous value.
        this.clearDescendants(0);
        this.root.value = value.values[0];
        let nextLevel = 1;

        this.delegate.getSubselects(this.root, value, (subselect: SingleSubselect) => {
            subselect.connect(this);
            subselect.value = value.values[nextLevel++];
            this.registerSubselect(subselect);
        });
    }

    onValueChange(parent: SingleSubselect): void {
        const value = parent.value;
        const level = this.getLevel(parent);

        if (value === null) {
            // Deselecting: remove any children from the branch,
            // but do NOT disconnect this subselect, since it is still visible and active.
            if (level !== -1) {
                this.clearDescendants(level);
            }
            return;
        }

        if (level !== -1) {
            // Re-selecting at an existing level: remove any descendants of the prior selection
            this.clearDescendants(level);
        } else {
            // New subselect joining the branch for the first time
            this.registerSubselect(parent);
        }

        // Load child subselect for the new selection, if applicable
        const selection = new TreeSelection(this.element, this.value, parent);
        if (!parent.getSubselectContainer(selection)) {
            return;
        }

        this.delegate.getSubselect(selection).then(subselect => {
            // Note: Subselect doesn't have a value yet, so it's not part of our branch
            subselect?.connect(this);
        });
    }

    private registerSubselect(subselect: SingleSubselect): void {
        // Notify ancestors (e.g. so they can remove their highlight), then append the new node.
        this.subselects.forEach(ancestor => ancestor.onDescendantSelected?.());
        this.subselects.push(subselect);
    }

    /**
     * Gets the level of the specified subselect within the current branch.
     * The root subselect will have level 0, its children will have level 1, and so on.
     * Returns -1 if the subselect does not (yet) have a value within the branch.
     **/
    private getLevel(subselect: SingleSubselect): number {
        return this.subselects.findIndex(branchSubselect => branchSubselect === subselect);
    }

    /**
     * Removes any child subselects below the given level, taking care of any necessary notifications and cleanup.
     * Specifically, notifies them of ancestor selection (e.g. to clear out dropdown options) and disconnects them.
     * Note that clearing does not necessarily imply removal from the DOM; some subselect types (e.g. dropdowns) may choose to remain visible but with no options when cleared, and this can be implemented in the onAncestorSelected callback.
     */
    private clearDescendants(parentLevel: number) {
        const removed = this.subselects.splice(parentLevel + 1);
        removed.forEach(subselect => {
            subselect.onAncestorSelected?.();
            subselect.disconnect();
        });
    }
}

